/**
 * useLiveKitSync - Manages LiveKit attribute synchronization for checkpoints
 *
 * Handles:
 * - Setting ongoing_checkpoint attribute
 * - Notifying tutor when conversation completes
 * - Handling raise_hand source on tutor_needed status
 */
import { useCallback } from "react"

import { bugsnagClient } from "../../shared/bugsnagClient"

/**
 * Un attribut qui ne part pas (la connexion vacille) ne bloque pas le checkpoint : il laisse un fil
 * d'Ariane, jamais une erreur.
 */
const traceAttributeFailure = (attribute) => (cause) => {
    bugsnagClient.leaveBreadcrumb("checkpoint: attribute not set", {
        attribute,
        reason: cause?.name ?? "unknown",
    })
}

/**
 * @param {Object} options
 * @param {Object} options.room - LiveKit room instance
 * @param {Function} options.setAttributesThrottled - Throttled attribute setter
 * @param {Function} options.triggerSamNotification - LiveKit event sender
 * @param {Object} options.user - Current user object
 * @param {Object} options.currentLiveSession - Current live session
 * @returns {Object} { markCheckpointActive, markCheckpointComplete, notifyTutor }
 */
export const useLiveKitSync = ({
    room = null,
    setAttributesThrottled = () => Promise.resolve(),
    triggerSamNotification = () => {},
    user = null,
    currentLiveSession = null
}) => {
    const hasLocalParticipant = room?.localParticipant

    /**
     * Mark the checkpoint as active in LiveKit
     */
    const markCheckpointActive = useCallback(() => {
        if (!hasLocalParticipant) return

        setAttributesThrottled({
            ongoing_checkpoint: "1",
            display_name: user?.display_name
        }).catch(traceAttributeFailure("ongoing_checkpoint"))
    }, [hasLocalParticipant, setAttributesThrottled, user?.display_name])

    /**
     * Mark the checkpoint as inactive in LiveKit
     */
    const markCheckpointInactive = useCallback(() => {
        if (!hasLocalParticipant) return

        setAttributesThrottled({
            ongoing_checkpoint: "",
            display_name: user?.display_name
        }).catch(traceAttributeFailure("ongoing_checkpoint"))
    }, [hasLocalParticipant, setAttributesThrottled, user?.display_name])

    /**
     * Mark that the student needs tutor help (raise hand from checkpoint)
     */
    const markTutorNeeded = useCallback(() => {
        if (!hasLocalParticipant) return

        setAttributesThrottled({
            raise_hand_source: "checkpoint",
            display_name: user?.display_name
        }).catch(traceAttributeFailure("raise_hand_source"))
    }, [hasLocalParticipant, setAttributesThrottled, user?.display_name])

    /**
     * Notify the tutor that the conversation has completed
     * @param {Object} conversationData - The completed conversation data
     */
    const notifyTutor = useCallback((conversationData) => {
        if (!currentLiveSession?.tutor?.id) return

        triggerSamNotification("agentConversationCompleted", {
            liveSessionId: currentLiveSession.id,
            studentId: user?.id,
            tutor_id: currentLiveSession.tutor?.id,
            displayName: user?.display_name,
            conversation: conversationData,
            timestamp: Date.now()
        })
    }, [
        currentLiveSession?.id,
        currentLiveSession?.tutor?.id,
        user?.id,
        user?.display_name,
        triggerSamNotification
    ])

    /**
     * Handle conversation completion - combines marking inactive + notifying tutor
     * @param {Object} conversationData - The conversation data
     */
    const handleConversationComplete = useCallback((conversationData) => {
        const status = conversationData?.conversation?.status
        const hasNoMoreResponses = !conversationData?.agent_message?.proposed_responses?.length

        // Mark checkpoint as inactive
        markCheckpointInactive()

        // If tutor needed, also mark raise hand
        if (status === "tutor_needed") {
            markTutorNeeded()
        }

        // Notify tutor if conversation is in a terminal state or has no more responses
        if (status === "tutor_needed" || status === "ended" || hasNoMoreResponses) {
            notifyTutor(conversationData)
        }
    }, [markCheckpointInactive, markTutorNeeded, notifyTutor])

    return {
        markCheckpointActive,
        markCheckpointInactive,
        markTutorNeeded,
        notifyTutor,
        handleConversationComplete,
        hasLiveKitConnection: hasLocalParticipant
    }
}

export default useLiveKitSync
